'use client';

function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from 'react';
import makeCancellable from 'make-cancellable-promise';
import clsx from 'clsx';
import invariant from 'tiny-invariant';
import warning from 'warning';
import * as pdfjs from 'pdfjs-dist';
import useDocumentContext from '../shared/hooks/useDocumentContext.js';
import usePageContext from '../shared/hooks/usePageContext.js';
import useResolver from '../shared/hooks/useResolver.js';
import { cancelRunningTask } from '../shared/utils.js';
export default function AnnotationLayer() {
  var documentContext = useDocumentContext();
  var pageContext = usePageContext();
  invariant(pageContext, 'Unable to find Page context.');
  var mergedProps = Object.assign(Object.assign({}, documentContext), pageContext);
  var imageResourcesPath = mergedProps.imageResourcesPath,
    linkService = mergedProps.linkService,
    onGetAnnotationsErrorProps = mergedProps.onGetAnnotationsError,
    onGetAnnotationsSuccessProps = mergedProps.onGetAnnotationsSuccess,
    onRenderAnnotationLayerErrorProps = mergedProps.onRenderAnnotationLayerError,
    onRenderAnnotationLayerSuccessProps = mergedProps.onRenderAnnotationLayerSuccess,
    page = mergedProps.page,
    pdf = mergedProps.pdf,
    renderForms = mergedProps.renderForms,
    rotate = mergedProps.rotate,
    _mergedProps$scale = mergedProps.scale,
    scale = _mergedProps$scale === void 0 ? 1 : _mergedProps$scale;
  invariant(pdf, 'Attempted to load page annotations, but no document was specified. Wrap <Page /> in a <Document /> or pass explicit `pdf` prop.');
  invariant(page, 'Attempted to load page annotations, but no page was specified.');
  invariant(linkService, 'Attempted to load page annotations, but no linkService was specified.');
  var _useResolver = useResolver(),
    _useResolver2 = _slicedToArray(_useResolver, 2),
    annotationsState = _useResolver2[0],
    annotationsDispatch = _useResolver2[1];
  var annotations = annotationsState.value,
    annotationsError = annotationsState.error;
  var layerElement = useRef(null);
  warning(Number.parseInt(window.getComputedStyle(document.body).getPropertyValue('--react-pdf-annotation-layer'), 10) === 1, 'AnnotationLayer styles not found. Read more: https://github.com/wojtekmaj/react-pdf#support-for-annotations');
  function onLoadSuccess() {
    if (!annotations) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    if (onGetAnnotationsSuccessProps) {
      onGetAnnotationsSuccessProps(annotations);
    }
  }
  function onLoadError() {
    if (!annotationsError) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    warning(false, annotationsError.toString());
    if (onGetAnnotationsErrorProps) {
      onGetAnnotationsErrorProps(annotationsError);
    }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect intentionally triggered on page change
  useEffect(function resetAnnotations() {
    annotationsDispatch({
      type: 'RESET'
    });
  }, [annotationsDispatch, page]);
  useEffect(function loadAnnotations() {
    if (!page) {
      return;
    }
    var cancellable = makeCancellable(page.getAnnotations());
    var runningTask = cancellable;
    cancellable.promise.then(function (nextAnnotations) {
      annotationsDispatch({
        type: 'RESOLVE',
        value: nextAnnotations
      });
    }).catch(function (error) {
      annotationsDispatch({
        type: 'REJECT',
        error: error
      });
    });
    return function () {
      cancelRunningTask(runningTask);
    };
  }, [annotationsDispatch, page]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Ommitted callbacks so they are not called every time they change
  useEffect(function () {
    if (annotations === undefined) {
      return;
    }
    if (annotations === false) {
      onLoadError();
      return;
    }
    onLoadSuccess();
  }, [annotations]);
  function onRenderSuccess() {
    if (onRenderAnnotationLayerSuccessProps) {
      onRenderAnnotationLayerSuccessProps();
    }
  }
  function onRenderError(error) {
    warning(false, "".concat(error));
    if (onRenderAnnotationLayerErrorProps) {
      onRenderAnnotationLayerErrorProps(error);
    }
  }
  var viewport = useMemo(function () {
    return page.getViewport({
      scale: scale,
      rotation: rotate
    });
  }, [page, rotate, scale]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Ommitted callbacks so they are not called every time they change
  useEffect(function renderAnnotationLayer() {
    if (!pdf || !page || !linkService || !annotations) {
      return;
    }
    var layer = layerElement.current;
    if (!layer) {
      return;
    }
    var clonedViewport = viewport.clone({
      dontFlip: true
    });
    var annotationLayerParameters = {
      accessibilityManager: null,
      // TODO: Implement this
      annotationCanvasMap: null,
      // TODO: Implement this
      annotationEditorUIManager: null,
      // TODO: Implement this
      div: layer,
      l10n: null,
      // TODO: Implement this
      page: page,
      structTreeLayer: null,
      // TODO: Implement this
      viewport: clonedViewport
    };
    var renderParameters = {
      annotations: annotations,
      annotationStorage: pdf.annotationStorage,
      div: layer,
      imageResourcesPath: imageResourcesPath,
      linkService: linkService,
      page: page,
      renderForms: renderForms,
      viewport: clonedViewport
    };
    layer.innerHTML = '';
    try {
      new pdfjs.AnnotationLayer(annotationLayerParameters).render(renderParameters);
      // Intentional immediate callback
      onRenderSuccess();
    } catch (error) {
      onRenderError(error);
    }
    return function () {
      // TODO: Cancel running task?
    };
  }, [annotations, imageResourcesPath, linkService, page, pdf, renderForms, viewport]);
  return _jsx("div", {
    className: clsx('react-pdf__Page__annotations', 'annotationLayer'),
    ref: layerElement
  });
}